import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import violenceStatsAction from './violenceStats.action.json' with { type: 'json' }
import { z } from 'zod'
import { dateOrIsoString } from '~/prix/types/date'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { select } from '~/prix/types/presets/select'
import { attendanceShape } from '../../attendance/stats/attendanceStats.definition'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

const expressionForCharacteristics = expression(
  attendanceShape.pick({
    // PROPRIEDADES BÁSICAS
    "citizen.age": true,
    "citizen.sex": true,
    "citizen.ageDecimal": true,
    "citizen.ageRange": true,
    "citizen.deceased": true,
    "citizen.originalId": true,
    
    // CONDIÇÕES DE SAÚDE
    "citizenHealth.isResponsibleFamilyMember": true,
    "citizenHealth.hasDiabetes": true,
    "citizenHealth.hasHypertension": true,
    "citizenHealth.hadStroke": true,
    "citizenHealth.hadHeartAttack": true,
    "citizenHealth.hasHeartDisease": true,
    "citizenHealth.hasRespiratoryDisease": true,
    "citizenHealth.hadKidneyDisease": true,
    "citizenHealth.hasLeprosy": true,
    "citizenHealth.hasTuberculosis": true,
    "citizenHealth.hadCancer": true,
    "citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions": true,
    "citizenHealth.hasAnyDisability": true,
    "citizenHealth.isPregnant": true,
    "citizenHealth.isBedridden": true,
    "citizenHealth.isDomiciled": true,

    // HÁBITOS E ESTILO DE VIDA
    "citizenHealth.smoker": true,
    "citizenHealth.consumesAlcoholicBeverages": true,
    "citizenHealth.takeDrugs": true,
    "citizenHealth.currentWeightStatus": true,
    "citizenHealth.eatHowManyTimesDay": true,

    // IDENTIFICAÇÃO E CARACTERÍSTICAS
    "citizenHealth.skinColorOrRace": true,
    "citizenHealth.nationality": true,
    "citizenHealth.educationLevel": true,
    "citizenHealth.employmentStatus": true,
    "citizenHealth.sexualOrientation": true,
    "citizenHealth.kinshipWithFamilyResponsible": true,

    // ESTRUTURA FAMILIAR
    "citizenHealth.unknownMother": true,
    "citizenHealth.unknownFather": true,
    "citizenHealth.attendsSchoolOrDaycare": true,

    // COMUNIDADE E PRÁTICAS
    "citizenHealth.memberOfTraditionalGroupsOrCommunity": true,
    "citizenHealth.takePartInCommunityGroups": true,

    // VULNERABILIDADE E BENEFÍCIOS
    "citizenHealth.homeless": true,
    "citizenHealth.homelessForHowManyTime": true,
    "citizenHealth.receivesBenefit": true,
    "citizenHealth.hasPrivateHealthInsurance": true,
  }).shape,
)
const expressionForAssertions = expression(
  attendanceShape.pick({
    "citizen.sex": true,
  }).shape,
)

export const violenceVariable: Variable = {
  key: 'attendanceViolence',
  actionDefinitionKey: violenceStatsAction.key,
  title: 'Violência',
  categoryKey: 'healthAttendances',
  unitSingular: 'registro de violência',
  unitPlural: 'registros de violências',
  grammaticalGender: 'female',
  timeType: 'incidence',
  shape: getZodSchemaInfo(attendanceShape),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.violence,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
     /* ATENDIMENTO */
    'attendanceDateTime',
    'professionalName',
    'healthUnitName',
    'teamName',
    'icd10Description',
    'icpc2Description',
    'weight',
    'height',
    'typeOfAttendance',
    'isScheduleReturn',
    'isScheduleForGroups',
    'isScheduleForNasf',
    'isScheduleForContinuedCare',
    'isEpisodeDischarge',
    'hasReferralToSpecializedService',
    'hasReferralToCaps',
    'hasReferralToHospitalization',
    'hasReferralToUrgency',
    'hasIntersectoralReferral',
    'hasReferralToHomeCareService',
     /* CIDADÃO */
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.latitude',
    'citizen.longitude',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.originalId',

   

    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',
  ],
  filterableBy: getZodSchemaInfo(
    attendanceShape.pick({
      "citizen.sex": true,
      "citizen.age": true,
      "citizen.deceased": true,
      
      "citizenHealth.isResponsibleFamilyMember": true,
      "citizenHealth.hasDiabetes": true,
      "citizenHealth.hasHypertension": true,
      "citizenHealth.hadStroke": true,
      "citizenHealth.hadHeartAttack": true,
      "citizenHealth.hasHeartDisease": true,
      "citizenHealth.hasRespiratoryDisease": true,
      "citizenHealth.hadKidneyDisease": true,
      "citizenHealth.hasLeprosy": true,
      "citizenHealth.hasTuberculosis": true,
      "citizenHealth.hadCancer": true,
      "citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions": true,
      "citizenHealth.hasAnyDisability": true,
      "citizenHealth.isPregnant": true,
      "citizenHealth.isBedridden": true,
      "citizenHealth.isDomiciled": true,

      "citizenHealth.smoker": true,
      "citizenHealth.consumesAlcoholicBeverages": true,
      "citizenHealth.takeDrugs": true,
      "citizenHealth.currentWeightStatus": true,
      "citizenHealth.eatHowManyTimesDay": true,

      "citizenHealth.skinColorOrRace": true,
      "citizenHealth.nationality": true,
      "citizenHealth.educationLevel": true,
      "citizenHealth.employmentStatus": true,
      "citizenHealth.sexualOrientation": true,
      "citizenHealth.kinshipWithFamilyResponsible": true,

      "citizenHealth.unknownMother": true,
      "citizenHealth.unknownFather": true,
      "citizenHealth.attendsSchoolOrDaycare": true,

      "citizenHealth.memberOfTraditionalGroupsOrCommunity": true,
      "citizenHealth.takePartInCommunityGroups": true,

      "citizenHealth.homeless": true,
      "citizenHealth.homelessForHowManyTime": true,
      "citizenHealth.receivesBenefit": true,
      "citizenHealth.hasPrivateHealthInsurance": true,
    }),
  ),
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 1,
        optimization: {
          minimumDays: 1095,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'trend',
        degree: 2,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 1,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(expressionForAssertions),
}

const violenceStatsDefinition = defineAction({
  ...violenceStatsAction,
  input: z.object({
    variable,
    highlight: expressionForCharacteristics.optional(),
    filter: expressionForCharacteristics.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(attendanceShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default violenceStatsDefinition
